import { Api, isDevelopment } from '@myre/common-conf';
import { isOkResponseBody } from '@myre/common-primitives';

import {
  HttpClient,
  HttpErrorResponse,
  HttpHandler,
  HttpRequest,
  HttpResponse,
  HttpResponseBase,
  type HttpEvent,
} from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  catchError,
  mergeMap,
  Observable,
  takeUntil,
  tap,
  throwError,
} from 'rxjs';

import { notifyInfo } from '../notification/store/notification.actions';
import { BaseAuthService } from '../services/auth/auth.service';

import { AbstractInterceptor } from './astract.interceptor';

const RETRYABLE_STATUS_CODES = [404, 500];

@Injectable({ providedIn: 'root' })
export class ApiErrorInterceptor extends AbstractInterceptor {
  private readonly _baseAuthService = inject(BaseAuthService);
  private readonly _httpClient = inject(HttpClient);
  private readonly _store = inject(Store<any>);

  protected override _handle(
    req: HttpRequest<any>,
    next: HttpHandler,
    api: Api,
  ): Observable<HttpEvent<any>> {
    if (!isDevelopment()) {
      return super._handle(req, next, api);
    }

    return next.handle(req).pipe(
      takeUntil(this._baseAuthService.onCancelPendingRequests()),
      tap(event => {
        this._handleDwhError(event);
      }),
      catchError(err => {
        if (this._isApiUnknownError(err)) {
          return this._handleApiError(req, err, api);
        }

        if (
          this._isApiForApp(api) &&
          this._isApiError(err) &&
          RETRYABLE_STATUS_CODES.includes(err.status)
        ) {
          return this._handleRetryableApiError(req, err, api);
        }

        return throwError(() => err);
      }),
    );
  }

  private _handleDwhError(event: HttpEvent<any>): void {
    if (!(event instanceof HttpResponse)) {
      return;
    }

    if (!isOkResponseBody(event.body)) {
      return;
    }

    if (event.body.meta['dwhError']) {
      this._store.dispatch(
        notifyInfo({
          message:
            'While the action was successful, there was DWH error(s) while handling dispatched messages',
        }),
      );
    }
  }

  private _handleApiError<R>(
    req: HttpRequest<R>,
    err: HttpErrorResponse,
    api: Api,
  ): Observable<HttpEvent<R>> {
    return this._baseAuthService.apiError(api, req.urlWithParams).pipe(
      mergeMap(retry => {
        if (retry) {
          return this._httpClient.request<R>(req);
        }

        return throwError(() => err);
      }),
    );
  }

  private _handleRetryableApiError<R>(
    req: HttpRequest<R>,
    err: HttpErrorResponse,
    api: Api,
  ): Observable<HttpEvent<R>> {
    return this._baseAuthService
      .retryableApiError(api, req.urlWithParams, err.status)
      .pipe(
        mergeMap(retry => {
          if (retry) {
            return this._httpClient.request<R>(req);
          }

          return throwError(() => err);
        }),
      );
  }

  private _isApiUnknownError(event: HttpResponseBase): boolean {
    return event instanceof HttpErrorResponse && event.status === 0;
  }

  private _isApiError(event: HttpResponseBase): boolean {
    return event instanceof HttpErrorResponse;
  }

  private _isApiForApp(api: Api): boolean {
    const app = window.location.host
      .split('.')[0]
      .split('-')[0]
      .trim()
      .toLowerCase();

    switch (app) {
      case 'app':
      case 'vad':
      case 'tenant': {
        return api === Api.AM;
      }
      case 'bp': {
        return api === Api.BP;
      }
    }
  }
}
